"use client";

import { useEffect, useId, useMemo, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Slider } from "~/components/ui/slider";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { cn } from "~/lib/utils";
import { FILTER_CONTROL_MUTED_ICON_CLASS } from "./filterControlStyles";
import { FilterControlButton } from "./FilterControl";
import { clampValue, getInputValue, normalizeRange } from "./functions";

// // // //

interface YieldRangeFilterProps {
    min?: number;
    max?: number;
    step?: number;
    value: [number, number];
    onValueChange: (value: [number, number]) => void;
    onApply?: (value: [number, number]) => void;
    data: number[];
}

function formatYieldValue(value: number) {
    return `${value.toFixed(1)}%`;
}

function formatRange(value: [number, number]) {
    return `${formatYieldValue(value[0])} - ${formatYieldValue(value[1])}`;
}

function formatTriggerLabel(value: [number, number], min: number, max: number) {
    if (value[0] === min && value[1] === max) {
        return `All APYs (${formatRange([min, max])})`;
    }

    return formatRange(value);
}

function useHistogramBins(data: number[], min: number, max: number) {
    return useMemo(() => {
        const binCount = 48;
        const binSize = (max - min) / binCount;
        const histogram = new Array<number>(binCount).fill(0);

        data.forEach((val) => {
            if (val >= min && val <= max) {
                const binIndex = Math.min(
                    Math.floor((val - min) / binSize),
                    binCount - 1,
                );
                histogram[binIndex]++;
            }
        });

        const maxCount = Math.max(...histogram, 1);
        return histogram.map((count) => count / maxCount);
    }, [data, min, max]);
}

function HistogramBars({
    bins,
    min,
    max,
    value,
}: {
    bins: number[];
    min: number;
    max: number;
    value: [number, number];
}) {
    const rangeStart = (value[0] - min) / (max - min);
    const rangeEnd = (value[1] - min) / (max - min);

    return (
        <div className="flex h-20 items-end gap-[3px] px-1">
            {bins.map((height, i) => {
                const percentStart = i / bins.length;
                const percentEnd = (i + 1) / bins.length;
                const isActive =
                    percentStart >= rangeStart && percentEnd <= rangeEnd + 0.01;

                return (
                    <div
                        key={i}
                        className={cn(
                            "flex-1 rounded-t-[2px] transition-colors duration-200",
                            !isActive && "bg-muted",
                            isActive && "bg-tertiary-foreground",
                        )}
                        style={{ height: `${Math.max(height * 100, 6)}%` }}
                    />
                );
            })}
        </div>
    );
}

function YieldNumberInput({
    label,
    value,
    min,
    max,
    onChange,
    placeholder,
}: {
    label: string;
    value: number;
    min: number;
    max: number;
    onChange: (value: number) => void;
    placeholder: string;
}) {
    const inputId = useId();
    const stepValue = 0.1;

    const handleStep = (direction: 1 | -1) => {
        const currentValue = Number.isFinite(value) ? value : min;
        const nextValue =
            Math.round((currentValue + direction * stepValue) * 10) / 10;
        onChange(clampValue(nextValue, min, max));
    };

    return (
        <div className="space-y-1">
            <label
                htmlFor={inputId}
                className="text-sm font-medium text-secondary-foreground"
            >
                {label}
            </label>
            <div className="relative">
                <Input
                    id={inputId}
                    type="number"
                    inputMode="decimal"
                    min={min}
                    max={max}
                    step={0.1}
                    value={Number.isFinite(value) ? value : ""}
                    placeholder={placeholder}
                    onChange={(event) =>
                        onChange(getInputValue(event.target.value, value))
                    }
                    className="h-10 pr-14 text-sm tabular-nums [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                />
                <div className="absolute inset-y-1 right-7 flex w-4 flex-col items-center justify-center">
                    <button
                        type="button"
                        className="flex h-3.5 w-4 items-center justify-center rounded-[2px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
                        disabled={value >= max}
                        aria-label={`Increase ${label.toLowerCase()} APY by 0.1%`}
                        onClick={() => handleStep(1)}
                    >
                        <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                        type="button"
                        className="flex h-3.5 w-4 items-center justify-center rounded-[2px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-40"
                        disabled={value <= min}
                        aria-label={`Decrease ${label.toLowerCase()} APY by 0.1%`}
                        onClick={() => handleStep(-1)}
                    >
                        <ChevronDown className="h-3 w-3" />
                    </button>
                </div>
                <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                    %
                </span>
            </div>
        </div>
    );
}

export function YieldRangeFilter({
    min = 0,
    max = 20,
    step = 0.1,
    value,
    onValueChange,
    onApply,
    data,
}: YieldRangeFilterProps) {
    const [open, setOpen] = useState(false);
    const [draftValue, setDraftValue] = useState<[number, number]>(value);
    const bins = useHistogramBins(data, min, max);
    const normalizedDraftValue = normalizeRange(draftValue, min, max);
    const defaultRange: [number, number] = [min, max];
    const hasDraftChanges =
        normalizedDraftValue[0] !== value[0] ||
        normalizedDraftValue[1] !== value[1];
    const isDefaultRange =
        normalizedDraftValue[0] === defaultRange[0] &&
        normalizedDraftValue[1] === defaultRange[1];

    useEffect(() => {
        if (open) {
            setDraftValue(value);
        }
    }, [open, value]);

    const updateDraftValue = (nextValue: [number, number]) => {
        setDraftValue(normalizeRange(nextValue, min, max));
    };

    const commitValue = (nextValue: [number, number]) => {
        const normalizedValue = normalizeRange(nextValue, min, max);
        onValueChange(normalizedValue);
        onApply?.(normalizedValue);
        setOpen(false);
    };

    const handleReset = () => {
        setDraftValue(defaultRange);
        commitValue(defaultRange);
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <FilterControlButton>
                    <span className="font-normal">
                        {formatTriggerLabel(value, min, max)}
                    </span>
                    <ChevronDown
                        className={`${FILTER_CONTROL_MUTED_ICON_CLASS} h-3 w-3`}
                    />
                </FilterControlButton>
            </PopoverTrigger>
            <PopoverContent
                className="w-[330px] rounded-lg p-4 shadow-xs"
                align="start"
            >
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-3">
                        <h3 className="text-sm font-normal text-foreground">
                            30D APY Range
                        </h3>
                    </div>

                    <div className="space-y-4">
                        <div className="space-y-2">
                            <HistogramBars
                                bins={bins}
                                min={min}
                                max={max}
                                value={normalizedDraftValue}
                            />
                            <Slider
                                min={min}
                                max={max}
                                step={step}
                                value={normalizedDraftValue}
                                onValueChange={(nextValue) =>
                                    updateDraftValue(
                                        nextValue as [number, number],
                                    )
                                }
                            />
                            <div className="flex items-center justify-between text-sm tabular-nums text-muted-foreground">
                                <span>{formatYieldValue(min)}</span>
                                <span>{formatYieldValue(max)}</span>
                            </div>
                        </div>

                        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-3">
                            <YieldNumberInput
                                label="Minimum"
                                value={normalizedDraftValue[0]}
                                min={min}
                                max={max}
                                placeholder="Min APY"
                                onChange={(nextMin) =>
                                    updateDraftValue([
                                        nextMin,
                                        normalizedDraftValue[1],
                                    ])
                                }
                            />
                            <span className="pb-2 text-sm text-tertiary-foreground">
                                -
                            </span>
                            <YieldNumberInput
                                label="Maximum"
                                value={normalizedDraftValue[1]}
                                min={min}
                                max={max}
                                placeholder="Max APY"
                                onChange={(nextMax) =>
                                    updateDraftValue([
                                        normalizedDraftValue[0],
                                        nextMax,
                                    ])
                                }
                            />
                        </div>

                        <div className="flex gap-2">
                            <Button
                                type="button"
                                className="h-9 min-w-0 flex-1 rounded-lg text-sm"
                                disabled={!hasDraftChanges}
                                onClick={() =>
                                    commitValue(normalizedDraftValue)
                                }
                            >
                                Apply
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                className="h-9 w-1/3 shrink-0 rounded-lg text-sm"
                                disabled={isDefaultRange}
                                onClick={handleReset}
                            >
                                Reset
                            </Button>
                        </div>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
